import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Users, Search, Filter, Download, ArrowUpDown, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { LenderRiskBadge } from "@/components/lenders/LenderRiskBadge";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";

const BorrowerStatusBadge = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      case "pending":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
      case "inactive":
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
      case "flagged":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };

  return (
    <Badge className={getStatusColor()} variant="outline">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

const columns = [
  {
    accessorKey: "name",
    header: "Borrower",
    cell: ({ row }) => {
      const initials = row.getValue("name")
        .split(" ")
        .map((n) => n[0])
        .join("");

      return (
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={row.original.avatar} alt={row.getValue("name")} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{row.getValue("name")}</div>
            <div className="text-xs text-muted-foreground">{row.original.email}</div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "credit_score",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0 hover:bg-transparent"
        >
          Credit Score
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const score = parseInt(row.getValue("credit_score"));
      let textColor = "text-gray-800";
      
      if (score >= 740) textColor = "text-green-600";
      else if (score >= 670) textColor = "text-green-500";
      else if (score >= 580) textColor = "text-amber-500";
      else textColor = "text-red-500";
      
      return <div className={`font-medium ${textColor}`}>{score}</div>;
    },
  },
  {
    accessorKey: "risk_level",
    header: "Risk Level",
    cell: ({ row }) => <LenderRiskBadge risk={row.getValue("risk_level")} />,
  },
  {
    accessorKey: "active_loans",
    header: "Active Loans",
  },
  {
    accessorKey: "total_borrowed",
    header: "Total Borrowed",
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue("total_borrowed"));
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
      return formatted;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <BorrowerStatusBadge status={row.getValue("status")} />,
  },
  {
    accessorKey: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const navigate = useNavigate();
      return (
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => {
            navigate(`/lender/borrowers/${row.original.id}`);
          }}
        >
          <Eye className="h-4 w-4" />
        </Button>
      );
    },
  },
];

const mockBorrowers = [
  {
    id: "1",
    name: "John Smith",
    email: "john.smith@example.com",
    avatar: "",
    credit_score: "745",
    risk_level: "conservative",
    active_loans: "2",
    total_borrowed: "150000",
    status: "active",
  },
  {
    id: "2",
    name: "Sarah Johnson",
    email: "sarah.j@example.com",
    avatar: "",
    credit_score: "680",
    risk_level: "moderate",
    active_loans: "1",
    total_borrowed: "75000",
    status: "active",
  },
  {
    id: "3",
    name: "Michael Davis",
    email: "michael.d@example.com",
    avatar: "",
    credit_score: "620",
    risk_level: "aggressive",
    active_loans: "3",
    total_borrowed: "85000",
    status: "flagged",
  },
  {
    id: "4",
    name: "Emma Wilson",
    email: "emma.w@example.com",
    avatar: "",
    credit_score: "790",
    risk_level: "conservative",
    active_loans: "1",
    total_borrowed: "200000",
    status: "active",
  },
  {
    id: "5",
    name: "Robert Brown",
    email: "robert.b@example.com",
    avatar: "",
    credit_score: "710",
    risk_level: "moderate",
    active_loans: "2",
    total_borrowed: "120000",
    status: "active",
  },
  {
    id: "6",
    name: "Lisa Anderson",
    email: "lisa.a@example.com",
    avatar: "",
    credit_score: "550",
    risk_level: "aggressive",
    active_loans: "1",
    total_borrowed: "30000",
    status: "inactive",
  },
  {
    id: "7",
    name: "David Miller",
    email: "david.m@example.com",
    avatar: "",
    credit_score: "650",
    risk_level: "moderate",
    active_loans: "0",
    total_borrowed: "0",
    status: "pending",
  },
];

const Borrowers = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterCriteria, setFilterCriteria] = useState({
    minCreditScore: "500",
    maxCreditScore: "850",
    riskLevel: "all"
  });

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const navigate = useNavigate();

  const { data: borrowers, isLoading } = useQuery({
    queryKey: ['borrowers'],
    queryFn: async () => {
      try {
        return mockBorrowers;
      } catch (error) {
        console.error('Error fetching borrowers:', error);
        toast({
          title: "Error",
          description: "Failed to load borrowers data",
          variant: "destructive",
        });
        return [];
      }
    }
  });

  const filteredBorrowers = borrowers?.filter(borrower => {
    const matchesSearch = 
      borrower.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      borrower.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTab = 
      activeTab === "all" || 
      borrower.status === activeTab;
    
    const matchesCreditScore = 
      parseInt(borrower.credit_score) >= parseInt(filterCriteria.minCreditScore) &&
      parseInt(borrower.credit_score) <= parseInt(filterCriteria.maxCreditScore);
    
    const matchesRiskLevel = 
      filterCriteria.riskLevel === "all" || 
      borrower.risk_level === filterCriteria.riskLevel;
    
    return matchesSearch && matchesTab && matchesCreditScore && matchesRiskLevel;
  }) || [];

  const handleExport = () => {
    toast({
      title: "Export Started",
      description: `Exporting borrower data as ${exportFormat.toUpperCase()}...`,
    });
    
    setTimeout(() => {
      toast({
        title: "Export Complete",
        description: `Borrowers data has been exported as ${exportFormat.toUpperCase()}.`,
      });
      setExportDialogOpen(false);
    }, 1500);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Borrowers</h1>
        <Users className="h-8 w-8 text-primary" />
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search borrowers..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filter
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <h4 className="font-medium">Filter Borrowers</h4>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Credit Score Range</label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number" 
                      placeholder="Min" 
                      value={filterCriteria.minCreditScore}
                      onChange={(e) => setFilterCriteria(prev => ({ ...prev, minCreditScore: e.target.value }))}
                      className="w-20" 
                    />
                    <span>to</span>
                    <Input 
                      type="number" 
                      placeholder="Max" 
                      value={filterCriteria.maxCreditScore}
                      onChange={(e) => setFilterCriteria(prev => ({ ...prev, maxCreditScore: e.target.value }))}
                      className="w-20" 
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Risk Level</label>
                  <Select 
                    value={filterCriteria.riskLevel}
                    onValueChange={(value) => setFilterCriteria(prev => ({ ...prev, riskLevel: value }))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Risk Level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Risk Levels</SelectItem>
                      <SelectItem value="conservative">Conservative</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="aggressive">Aggressive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setFilterCriteria({
                        minCreditScore: "500",
                        maxCreditScore: "850",
                        riskLevel: "all"
                      });
                    }}
                  >
                    Reset
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setIsFilterOpen(false)}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={() => setExportDialogOpen(true)}
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="pb-0">
          <CardTitle>
            <Tabs defaultValue="all" onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All Borrowers</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="flagged">Flagged</TabsTrigger>
                <TabsTrigger value="pending">Pending</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredBorrowers.length > 0 ? (
            <DataTable 
              columns={columns} 
              data={filteredBorrowers}
            />
          ) : (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No borrowers found matching your criteria</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Borrower Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Borrowers</span>
                <span className="font-medium">{borrowers?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active Borrowers</span>
                <span className="font-medium">{borrowers?.filter(b => b.status === "active").length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Average Credit Score</span>
                <span className="font-medium">
                  {borrowers?.length 
                    ? Math.round(borrowers.reduce((sum, b) => sum + parseInt(b.credit_score), 0) / borrowers.length) 
                    : 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Risk Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Conservative</span>
                <span className="font-medium">{borrowers?.filter(b => b.risk_level === "conservative").length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Moderate</span>
                <span className="font-medium">{borrowers?.filter(b => b.risk_level === "moderate").length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Aggressive</span>
                <span className="font-medium">{borrowers?.filter(b => b.risk_level === "aggressive").length || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Loan Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Active Loans</span>
                <span className="font-medium">
                  {borrowers?.reduce((sum, b) => sum + parseInt(b.active_loans), 0) || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Amount</span>
                <span className="font-medium">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 0,
                  }).format(borrowers?.reduce((sum, b) => sum + parseFloat(b.total_borrowed), 0) || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Avg. Loan Size</span>
                <span className="font-medium">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 0,
                  }).format((borrowers?.reduce((sum, b) => sum + parseFloat(b.total_borrowed), 0) || 0) / 
                    (borrowers?.reduce((sum, b) => sum + parseInt(b.active_loans), 0) || 1))}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export Borrowers Data</DialogTitle>
            <DialogDescription>
              Choose a format to export the borrowers data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select defaultValue="csv" value={exportFormat} onValueChange={setExportFormat}>
              <SelectTrigger>
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="excel">Excel</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleExport}>
                Export
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Borrowers;
